import {
  useContext,
  useEffect,
  useState } from "react";
import { NavLink } from "react-router-dom";
import { Alert,
  Badge,
  Button,
  Flex,
  Spin,
  Switch,
} from "antd";
import { BellOutlined, BulbFilled, BulbOutlined, MenuOutlined } from "@ant-design/icons";
import Marquee from "react-fast-marquee";
import BrandLogo from "../../assets/img/ShinkoBrandLogo.png";
import { endSession, loadAllUser, loadUser, useSession } from "../../auth/session";
import { logoutUser } from "../../api/login/login";
import { ThemeContext } from "../../contexts/ThemeContext";
import { getDisplayNameFromUser } from "../../utils/userDisplayName";
import { useNotifications } from "./useNotifications";
import NotificationDrawer from "./NotificationDrawer";
import UserMenu from "./UserMenu";
import MobileUserDrawer from "./MobileUserDrawer";
import { spacing } from "../../theme/tokens";
import StatusTag from "../cms/StatusTag";

/**
 * Khóa logo theo CHIỀU CAO, không phải chiều rộng: header cố định 64px nên height
 * mới là trục quyết định. Khóa width thì đổi file logo là tỉ lệ tự nhảy (bản cũ
 * ratio 3.26 → bản Shinko 2.66, width 117 hóa ra cao 44px, sát mép header).
 *
 * 48px trông "vượt chuẩn" so với quy tắc logo ≈ 56% chiều cao header, nhưng đúng:
 * file ShinkoBrandLogo.png có sẵn 18.1% whitespace trên + 16.2% dưới, tức 34%
 * chiều cao là padding quang học. Ở 48px thì chữ SHINKO (48% chiều cao file) cao
 * 23px và vẫn thở ~16px mỗi bên. Hạ về 36 là chữ chỉ còn 17px, nhìn hụt hẳn.
 * 52 là trần — quá đó mép chữ chạm viền header. Có bản wordmark crop sát, bỏ
 * tagline thì đổi lại còn 26–28.
 */
const LOGO_STYLE = { height: 48, width: "auto", display: "block", objectFit: "contain" };

const ROLE_BADGE = {
  ADMIN: { text: "Admin", color: "red" },
  SHOP: { text: "Shop", color: "blue" },
  STAFF: { text: "Nhân viên", color: "green" },
  BRANCH_MANAGER: { text: "Quản lý", color: "purple" },
  CUSTOMER: { text: "Khách hàng", color: "cyan" },
};

/**
 * Nội dung thanh trên cùng: logo, theme toggle, vai trò, tên, avatar + đăng xuất.
 *
 * Trước đây file này 537 dòng và tự giữ luôn state thông báo, drawer mobile, menu
 * avatar cùng ~30 dòng code cũ để comment. Đã tách ra: `useNotifications`,
 * `NotificationDrawer`, `UserMenu`, `MobileUserDrawer` — ở đây chỉ còn bố cục.
 */
export default function HeaderConek() {
  const { user, loadUser: isLoadingUser } = useSession();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [notiOpen, setNotiOpen] = useState(false);

  const isCustomer = user?.data?.type === "CUSTOMER";
  const notifications = useNotifications();

  /**
   * Nạp hồ sơ. `loadUser()` tự tôn trọng cache 5 phút nên gọi thẳng — không cần
   * đọc state ra so timestamp như trước (đoạn đó phải `store.getState()` để tránh
   * dependency loop, giờ không còn store nào để đọc).
   */
  useEffect(() => {
    loadUser()
      .then((data) => {
        if (data?.data?.type === "ADMIN") return loadAllUser();
      })
      .catch(() => {
        // 401 đã do interceptor lo. Lỗi khác thì giữ hồ sơ đang có trong store.
      });
  }, []);

  useEffect(() => {
    if (isCustomer) notifications.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer]);

  const logOut = async () => {
    try {
      await logoutUser();
    } catch {
      // Thu hồi token phía server thất bại vẫn phải dọn phía client.
    } finally {
      endSession();
    }
  };

  const nameLabel = getDisplayNameFromUser(user?.data, user?.data?.username || "Người dùng");
  const roleBadge = ROLE_BADGE[user?.data?.type];
  const homePath = isCustomer ? "/info-point-store" : "/";

  return (
    <>
      {isCustomer && (
        <NotificationDrawer
          open={notiOpen}
          onClose={() => setNotiOpen(false)}
          notifications={notifications}
        />
      )}

      <MobileUserDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        nameLabel={nameLabel}
        isCustomer={isCustomer}
        unreadCount={notifications.unreadCount}
        onOpenNotifications={() => setNotiOpen(true)}
        onLogout={logOut}
      />

      <Flex align="center" style={{ flexShrink: 0 }}>
        <NavLink to={homePath} style={{ display: "flex", alignItems: "center" }}>
          <img style={LOGO_STYLE} src={BrandLogo} alt="Shinko" />
        </NavLink>
      </Flex>

      <Button type="text" onClick={() => setMobileOpen(true)} className="menu-button">
        <MenuOutlined style={{ fontSize: 20 }} />
      </Button>

      {user?.data && !user.data.phone && (
        <Alert
          banner
          message={
            <Marquee pauseOnHover gradient={false}>
              Vui lòng cập nhật số điện thoại! <NavLink to="/editUser">Đến</NavLink>
            </Marquee>
          }
        />
      )}

      <Flex className="header-content" align="center" justify="space-between" gap="middle">
        <Flex className="header_right" align="center" justify="space-between" gap="middle">
          {/* <Flex align="center" gap={6} style={{ marginRight: spacing[2] }}>
            <BulbOutlined style={{ fontSize: 13, color: isDarkMode ? "#64748B" : "#F59E0B" }} />
            <Switch size="small" checked={isDarkMode} onChange={toggleTheme} />
            <BulbFilled style={{ fontSize: 13, color: isDarkMode ? "#93C5FD" : "#64748B" }} />
          </Flex> */}

          {isCustomer && (
            <Button type="text" shape="circle" onClick={() => setNotiOpen(true)}>
              <Badge count={notifications.unreadCount}>
                <BellOutlined style={{ fontSize: 18 }} />
              </Badge>
            </Button>
          )}

          <Flex align="center" gap={spacing[2]}>
            {isLoadingUser && !user ? (
              <Spin size="small" />
            ) : (
              <>
                {roleBadge && (
                  <StatusTag style={{ fontSize: 11, lineHeight: "18px" }}>{roleBadge.text}</StatusTag>
                )}
                <NavLink
                  to="/editUser"
                  style={{ color: isDarkMode ? "#93C5FD" : "#2563EB", fontWeight: 500, fontSize: 14 }}
                >
                  {nameLabel}
                </NavLink>
              </>
            )}
          </Flex>

          <UserMenu user={user} onLogout={logOut} />
        </Flex>
      </Flex>
    </>
  );
}
